import React from 'react';
import { X } from 'lucide-react';
import { MobileTutorialStep } from './mobileTutorialContent';

interface MobileTutorialTextBoxProps {
  step: MobileTutorialStep;
  totalSteps: number;
  onSkip: () => void;
  onAdvance?: (e: React.MouseEvent) => void;
}

export const MobileTutorialTextBox: React.FC<MobileTutorialTextBoxProps> = ({
  step,
  totalSteps,
  onSkip,
  onAdvance,
}) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[10000] animate-slide-up">
      <div
        className="bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl border-t-2 border-blue-500 max-h-[40vh] overflow-y-auto cursor-pointer"
        onClick={onAdvance}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {/* Progress Dots */}
            <div className="flex gap-1.5">
              {Array.from({ length: totalSteps }).map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === step.id - 1
                      ? 'w-6 bg-blue-500'
                      : 'w-2 bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
              {step.id} / {totalSteps}
            </span>
          </div>

          {/* Skip Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSkip();
            }}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200 ease-theme"
            aria-label="Skip tutorial"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            {step.title}
          </h2>
          <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
            {step.description}
          </p>

          {/* Tap to continue indicator */}
          {!step.isLast && (
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                Tap anywhere to continue
              </p>
            </div>
          )}

          {/* Finish indicator for last step */}
          {step.isLast && (
            <div className="mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSkip();
                }}
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors duration-200 ease-theme"
              >
                Start Playing
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
